// Components/AIChat/AIChat.jsx
import React, { Fragment, useEffect, useRef, useState, useCallback } from "react";
import { ListarMensagensConversaAIChat, EnviarMensagemAIChat } from "../../Services/AI";
import { NewGuid, FormatDateTimeChat } from "../../Utils/globalFunctions";
import "./AIChat.css";

export default function AIChat({ titulo, chatConversaId, conversationId, onSentSuccess }) {
    const [inputText, setInputText] = useState("");
    const [messages, setMessages] = useState([]);
    const [loadingHistorico, setLoadingHistorico] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState(null);

    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = useCallback((behavior = "smooth") => {
        if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior });
    }, []);

    const focusInput = useCallback(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    const scrollAndFocus = useCallback(() => {
        scrollToBottom("smooth");
        focusInput();
    }, [scrollToBottom, focusInput]);

    const carregarHistorico = useCallback(async () => {

        if (!chatConversaId) {
            setMessages([]);
            setTimeout(() => focusInput(), 0);
            return;
        }

        try {
            setErro(null);
            setLoadingHistorico(true);

            const { success, mensagens, message } = await ListarMensagensConversaAIChat(chatConversaId, 200);

            if (!success) {
                setErro(message || "Não foi possível carregar as mensagens.");
                setMessages([]);
                return;
            }

            // Sua API hoje pode vir em ASC ou DESC dependendo do repo.
            // Vamos garantir ASC aqui no front pra renderizar certinho:
            const ordenadas = (mensagens || []).slice().sort((a, b) => {
                const da = new Date(a.createdAt).getTime();
                const db = new Date(b.createdAt).getTime();
                // Se createdAt não for ISO, Date pode falhar; nesse caso mantém ordem original
                if (Number.isNaN(da) || Number.isNaN(db)) return 0;
                return da - db;
            });

            setMessages(ordenadas);
            // No carregamento, scroll "auto" pra não animar demais
            setTimeout(() => scrollToBottom("auto"), 0);
            setTimeout(() => focusInput(), 0);
        } catch (e) {
            console.error("Erro ao carregar histórico AIChat:", e);
            setErro("Não foi possível carregar as mensagens.");
            setMessages([]);
        } finally {
            setLoadingHistorico(false);
        }
    }, [chatConversaId, scrollToBottom, focusInput]);

    useEffect(() => {
        // quando troca de conversa
        setInputText("");
        setMessages([]);
        if (chatConversaId) carregarHistorico();
    }, [chatConversaId, carregarHistorico]);

    useEffect(() => {
        // quando mensagens mudam
        scrollToBottom("auto");
    }, [messages, scrollToBottom]);

    const handleSend = async () => {
        if (enviando) return;

        const text = (inputText || "").trim();
        if (!text) return;

        // messageId obrigatório para idempotência
        const messageId = NewGuid();

        setEnviando(true);
        setErro(null);

        // UI otimista: adiciona a msg "in" local
        const localIn = {
            chatMensagemId: `local-${messageId}`,
            messageId,
            direction: "in",
            text,
            createdAt: new Date().toISOString(),
            __local: true
        };

        setMessages((prev) => [...prev, localIn]);
        setInputText("");
        scrollAndFocus();

        try {
            const { success, data, message } = await EnviarMensagemAIChat(conversationId, messageId, text);

            if (!success) {
                setErro(message || "Falha ao enviar mensagem.");
                // marca como erro (sem remover pra não confundir o usuário)
                setMessages((prev) =>
                    prev.map((m) => (m.messageId === messageId ? { ...m, __error: true } : m))
                );
                setEnviando(false);
                return;
            }

            // adiciona resposta "out"
            const localOut = {
                chatMensagemId: `local-out-${messageId}`,
                messageId: null,
                direction: "out",
                text: data?.replyText || "",
                createdAt: data?.serverTime || new Date().toISOString(),
                __local: true
            };

            setMessages((prev) => [...prev, localOut]);
            setTimeout(() => scrollAndFocus(), 0);
            if (typeof onSentSuccess === "function") {
                onSentSuccess();
            }

        } catch (e) {
            console.error("Erro ao enviar mensagem AIChat:", e);
            setErro("Falha ao enviar mensagem.");
            setMessages((prev) =>
                prev.map((m) => (m.messageId === messageId ? { ...m, __error: true } : m))
            );
        } finally {
            setEnviando(false);
            setTimeout(() => focusInput(), 0);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") handleSend();
    };

    return (
        <div className="aichat-chat">
            <div className="aichat-chat-banner">
                <div className="aichat-chat-title">Olivia • Assegurou</div>
                <div className="aichat-chat-subtitle">{titulo}</div>
            </div>

            <div className="aichat-chat-messages">
                {loadingHistorico && (
                    <div className="aichat-chat-info">Carregando histórico...</div>
                )}

                {erro && (
                    <div className="aichat-chat-erro">
                        {erro}
                    </div>
                )}

                {!loadingHistorico && messages.length === 0 && !erro && (
                    <div className="aichat-chat-info">Envie uma mensagem para começar.</div>
                )}

                {messages.map((m) => {
                    const isIn = m.direction === "in";
                    return (
                        <div
                            key={m.chatMensagemId}
                            className={`aichat-bubble ${isIn ? "in" : "out"} ${m.__error ? "error" : ""}`}
                            title={m.__error ? "Falha ao enviar" : ""}
                        >
                            <div className="aichat-bubble-text">{m.text}</div>
                            <div className="aichat-bubble-time">
                                {FormatDateTimeChat(m.createdAt)}
                            </div>
                        </div>
                    );
                })}

                <div ref={bottomRef} />
            </div>

            <div className="aichat-chat-input">
                <input
                    type="text"
                    className="aichat-chat-text-input"
                    placeholder="Digite uma mensagem..."
                    value={inputText}
                    disabled={enviando}
                    ref={inputRef}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={enviando ? null : onKeyDown}
                />

                <button
                    className="aichat-chat-send-button"
                    onClick={enviando ? null : handleSend}
                    disabled={enviando}
                    title="Enviar"
                >
                    ➤
                </button>
            </div>
        </div>
    );
}
